import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import useEventListener from '@use-it/event-listener';
import chroma from 'chroma-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInstanceContext } from '../context/instance/instanceContext';
import { useSocketSend } from '../context/webSocket/webSocketContext';
import { ArrowUpThickIcon } from '../icons/arrowUpThickIcon';
import { setIsKeyboardCapturingInputActive } from '../redux/actions/commonActions';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { RED_COLOR } from '../styles/colors';
import { getInstanceSelector } from '../utils/uiUtils';
const CONTAINER_CSS = css `
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const TEXT_FIELD_CSS = css `
  & .MuiOutlinedInput-root {
    background-color: rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    line-height: 1.1;
  }
`;
const ADMIN_CHAT_TEXT_FIELD_CSS = css(TEXT_FIELD_CSS, css `
    & .MuiOutlinedInput-root {
      background-color: ${chroma(RED_COLOR).brighten(1.25).desaturate(1.25).css()};
    }
  `);
export const MessageInput = ({ type, messageInputHandler, label, sendButtonTitle }) => {
    const dispatch = useAppDispatch();
    const isAdmin = useAppSelector((state) => state.selfData.isAdmin);
    const isModalOpen = useAppSelector((state) => state.messageBoxes.messageBoxes.length > 0);
    const [composingMessage, setComposingMessage] = useState('');
    const socketSend = useSocketSend();
    const { instanceId, numInstances } = useInstanceContext();
    const textfieldDivRef = useRef(null);
    useEffect(() => {
        dispatch(setIsKeyboardCapturingInputActive(true));
        return () => {
            dispatch(setIsKeyboardCapturingInputActive(false));
        };
    }, [dispatch]);
    const onClickSend = useCallback(() => {
        const message = composingMessage.trim();
        if (message) {
            messageInputHandler(socketSend, message, dispatch);
        }
        setComposingMessage('');
    }, [composingMessage, messageInputHandler, socketSend, dispatch]);
    const onInputKeyDown = useCallback((event) => {
        if (event.key === 'Enter') {
            onClickSend();
            // prevent default to prevent the line break from being added to the input value:
            event.preventDefault();
        }
    }, [onClickSend]);
    /**
     * Handler to maybe redirect focus to the chat input field when it isn't initially focused.
     * This way, a user can start chatting without first clicking on the chat input field.
     */
    const onAnyKeyDown = useCallback((event) => {
        if (numInstances > 1) {
            // Disable redirecting focus if running more than one instance, to avoid any confusion about what should
            // get focused.
            return;
        }
        if (event.metaKey) {
            // Meta key is already reserved for betting key combos, we so this is not a key combo that should redirect focus.
            return;
        }
        if (event.key && event.key.length > 1) {
            // We only want to redirect focus to the chat input if it is a printable character (not a modifier key).
            // An easy way to determine this is just to look at the length of the key value.
            // A key of length 1 is probably a single letter and not 'F4' or 'Enter' or 'ArrowRight', etc.
            // It may not be 100% what we want, but it's probably really close.
            // See https://stackoverflow.com/a/49612427
            // TODO: Allow 'Backspace' and maybe others?
            return;
        }
        const textAreaElement = textfieldDivRef.current?.querySelector('textarea');
        if (!textAreaElement || document.activeElement === textAreaElement) {
            return;
        }
        const totalNumTextInputs = document.querySelectorAll(`${getInstanceSelector(instanceId)} input`).length;
        if (totalNumTextInputs > 0) {
            // Text/password inputs other than the chat input are in this app instance, so don't assume we want to redirect focus.
            return;
        }
        if (isModalOpen) {
            return;
        }
        textAreaElement.focus();
    }, [instanceId, numInstances, isModalOpen]);
    useEventListener('keydown', onAnyKeyDown);
    const onChangeComposingMessage = (event) => {
        setComposingMessage(event.target.value);
    };
    // style the input if it appears we are composing an admin channel message:
    const textFieldCss = type === 'chat' && isAdmin && composingMessage.startsWith('`') ? ADMIN_CHAT_TEXT_FIELD_CSS : TEXT_FIELD_CSS;
    return (_jsxs("div", { css: CONTAINER_CSS, children: [_jsx(TextField, { "data-testid": 'message input', css: textFieldCss, autoComplete: 'off', ref: textfieldDivRef, type: 'text', variant: 'outlined', fullWidth: true, placeholder: label, value: composingMessage, onChange: onChangeComposingMessage, onKeyDown: onInputKeyDown, size: 'small', multiline: true, maxRows: 3 }), _jsx(IconButton, { onClick: onClickSend, size: 'small', title: sendButtonTitle, children: _jsx(ArrowUpThickIcon, {}) })] }));
};
